import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, BarChart3, AlertTriangle, RefreshCw, MapPin, TrendingUp, Truck, Database, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedFeaturesSection from "@/components/RelatedFeaturesSection";
import FAQSection from "@/components/FAQSection";

const Inventory = () => {
  useSEO({
    title: 'Inventory Management Software | Ommni - Real-Time Stock Control',
    description: 'Manage inventory across all locations with real-time tracking, automated replenishment, and demand forecasting. Prevent stockouts and optimize inventory levels with Ommni.',
    keywords: 'inventory management, stock control, inventory tracking, warehouse management, inventory optimization',
    canonical: 'https://ommni.io/features/inventory',
  });

  const features = [
    {
      icon: Package,
      title: "Real-Time Tracking",
      description: "Monitor inventory levels across all locations with real-time updates and accurate stock counts.",
    },
    {
      icon: MapPin,
      title: "Multi-Location Management",
      description: "Manage inventory across warehouses, stores, and fulfillment centers from a single dashboard.",
    },
    {
      icon: RefreshCw,
      title: "Automatic Synchronization",
      description: "Keep inventory synced across all sales channels to prevent overselling and stockouts.",
    },
    {
      icon: AlertTriangle,
      title: "Low Stock Alerts",
      description: "Receive automatic notifications when inventory reaches reorder points.",
    },
    {
      icon: BarChart3,
      title: "Demand Forecasting",
      description: "Predict future inventory needs with AI-powered demand forecasting and analytics.",
    },
    {
      icon: TrendingUp,
      title: "Performance Metrics",
      description: "Track inventory turnover, carrying costs, and stock performance with detailed reports.",
    },
  ];

  const capabilities = [
    {
      title: "Stock Transfers",
      description: "Move inventory between locations easily with automated transfer workflows.",
    },
    {
      title: "Lot & Serial Tracking",
      description: "Track individual items with lot numbers and serial numbers for complete traceability.",
    },
    {
      title: "Inventory Reservations",
      description: "Reserve stock for pending orders while maintaining accurate available-to-sell counts.",
    },
    {
      title: "Cycle Counting",
      description: "Schedule regular inventory audits without disrupting operations.",
    },
    {
      title: "Safety Stock Management",
      description: "Maintain optimal safety stock levels to balance availability and carrying costs.",
    },
    {
      title: "Batch Operations",
      description: "Update inventory for thousands of SKUs simultaneously with bulk import tools.",
    },
  ];

  const faqs = [
    {
      question: "How does real-time inventory tracking work?",
      answer: "Ommni's inventory management system syncs automatically with all your sales channels, warehouses, and fulfillment centers. Every sale, return, or stock movement updates inventory counts instantly across your entire network."
    },
    {
      question: "Can I manage inventory across multiple warehouses and store locations?",
      answer: "Yes, Ommni supports unlimited locations including warehouses, retail stores, distribution centers, and third-party fulfillment locations. You can view and manage inventory for each location individually or collectively."
    },
    {
      question: "What happens when inventory runs low?",
      answer: "Ommni automatically sends low stock alerts when inventory reaches your defined reorder points. You can configure different thresholds for each product and location, and set up automated purchase order creation for restocking."
    },
    {
      question: "Does Ommni prevent overselling?",
      answer: "Yes, Ommni maintains accurate available-to-sell (ATS) counts by factoring in reserved inventory for pending orders. This prevents overselling across all sales channels while maximizing inventory availability."
    },
    {
      question: "Can I track inventory by lot number or serial number?",
      answer: "Yes, Ommni supports both lot tracking and serial number tracking. This is essential for industries requiring traceability, expiration date management, or warranty tracking."
    },
    {
      question: "How does Ommni help with demand forecasting?",
      answer: "Ommni uses historical sales data, seasonal trends, and AI-powered algorithms to predict future demand. This helps you maintain optimal stock levels, reduce carrying costs, and prevent stockouts during peak periods."
    }
  ];

  return (
    <PageLayout>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Ommni Inventory Management",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Take complete control of your inventory with real-time visibility, automated workflows, and intelligent forecasting across all locations and channels.",
          "offers": {
            "@type": "Offer",
            "price": "500",
            "priceCurrency": "USD",
            "description": "Starting at $500/month"
          },
          "provider": {
            "@type": "Organization",
            "name": "Ommni",
            "url": "https://ommni.io"
          },
          "featureList": [
            "Real-Time Tracking",
            "Multi-Location Management",
            "Automatic Synchronization",
            "Low Stock Alerts",
            "Demand Forecasting",
            "Performance Metrics"
          ]
        })}
      </script>
      <div className="px-4 pb-24">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Package className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Inventory Management
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Take complete control of your inventory with real-time visibility, automated workflows, 
              and intelligent forecasting across all locations and channels.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Start Free Trial</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Schedule Demo</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Core Inventory Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 gradient-card">
                  <div className="mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Capabilities Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Advanced Capabilities</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {capabilities.map((capability, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-semibold mb-2">{capability.title}</h3>
                  <p className="text-sm text-muted-foreground">{capability.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Related Features */}
          <RelatedFeaturesSection
            features={[
              {
                icon: Truck,
                title: "Order Management",
                description: "Fulfill orders with intelligent routing based on real-time inventory data.",
                link: "/features/dom",
              },
              {
                icon: Database,
                title: "Product Information Management",
                description: "Connect inventory data with complete product information across channels.",
                link: "/features/pim",
              },
              {
                icon: Workflow,
                title: "Workflow Automation",
                description: "Automate inventory updates and low stock alerts with OmmniFlow.",
                link: "/features/ommni-flow",
              },
            ]}
          />

          {/* FAQ Section */}
          <FAQSection 
            faqs={faqs}
            title="Frequently Asked Questions"
            description="Common questions about Ommni's Inventory Management system."
          />

          {/* CTA Section */}
          <Card className="p-12 bg-primary/5 border-2 border-primary/20 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Optimize Your Inventory Operations
            </h2>
            <p className="text-foreground/80 max-w-2xl mx-auto mb-8 text-lg">
              Join thousands of retailers who trust Ommni to manage their inventory efficiently, 
              reduce costs, and never miss a sale due to stockouts.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default Inventory;
